import React, { useRef } from 'react';
import styled from 'styled-components';
import { white } from '@peloton/colors';
import { media, hoverTransition } from '@peloton/styles';
import { gray1, slate3 } from '@engage/colors';
import { If } from '@engage/conditional-render';
import { useReduxAction } from '@engage/redux';
import { spaces } from '@engage/styles';
import { label2Large } from '@engage/typography';
import BookmarkLottie from '@members/animations/lottie/data/bookmark.json';
import LottieAnimation from '@members/animations/lottie/LottieAnimation';
import { MembersCopy } from '@members/copy';
import { Members } from '@members/copy/models.generated/Members';
import { Feature, useFeatureToggle } from '@members/feature-toggles';
import { dismissBookmark } from '@members/notifications/redux';

const BookmarkToast: React.FC<React.PropsWithChildren<Props>> = ({
  pageName,
  isBookmarkEnabled = false,
  children,
}) => {
  const container = useRef<HTMLDivElement>((null as any) as HTMLDivElement);
  const onClose = useReduxAction(dismissBookmark);
  const isBookmarkFeatureEnabled = useFeatureToggle(Feature.Bookmark_page);

  return (
    <If condition={isBookmarkFeatureEnabled}>
      {isBookmarkEnabled && (
        <BookmarkContainer ref={container} role="presentation">
          <StyledContent>
            <StyledMessageAndIcon>
              <IconContainer>
                <LottieAnimation
                  style={{ width: 25 }}
                  animationData={BookmarkLottie}
                  loop={false}
                />
              </IconContainer>
              <ToastText role="alert">{children}</ToastText>
            </StyledMessageAndIcon>
            <ButtonContainer type="button">
              <GotItButton onClick={() => onClose(pageName)}>
                <MembersCopy id={Members.Done} />
              </GotItButton>
            </ButtonContainer>
          </StyledContent>
        </BookmarkContainer>
      )}
    </If>
  );
};

const BookmarkContainer = styled.div`
  position: fixed;
  width: 360px;
  top: ${spaces.medium}px;
  right: 0;
  left: 0;
  margin: 0 auto;

  ${media.tablet`
    margin-right: 24px;
    width: 515px;
    right: 0;
  `}

  overflow: hidden;
  background-color: ${white};
  box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.1);
  z-index: 4;
  border-radius: ${spaces.xSmall}px;

  ${media.desktop`
    cursor: pointer;
  `}
`;

const ButtonContainer = styled.button`
  display: flex;
  align-items: center;
`;

const StyledContent = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px 0 16px;

  ${media.tablet`
    ${hoverTransition({
      property: 'background',
      to: 'rgba(0, 0, 0, 0.05)',
    })}
  `}
`;

export const StyledMessageAndIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
  padding: ${spaces.medium}px 0;
`;

const IconContainer = styled.div`
  margin-right: 12px;
`;

const GotItButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 10px;
  font-size: 12px;
  white-space: nowrap;
  background: ${gray1};
  border-radius: 8px;
  font-weight: 500;
  line-height: 16px;
  width: 96px;
`;

const ToastText = styled.span`
  display: flex;
  color: ${slate3};
  align-items: center;
  ${label2Large};
  min-height: ${spaces.large}px;
`;

type Props = {
  isBookmarkEnabled?: boolean;
  pageName: string;
};

export default BookmarkToast;
